/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { Component, ComponentType } from 'react';
import autobind from 'class-autobind';
import shallowEqual from './helpers/shallowEqual';
import { resolveKey } from './helpers/resolveKey';
import { showDialog } from '../components/App/AppDialog';
import { showSuccess, showError } from '../components/App/AppAlert';
import DataStore, { DataStoreInstance } from './DataStore';
// This must be imported before dataFetcher because:
//   dataFetcher > resources > FormModel > FormElements >
//   MarketSelectorElement > MarketSelectorFormView > connectData
// which causes a circular dependency.
import { ResourceKey, ResourceTypeForKey } from './constants/resources';

import {
  hasFetchEndpoint,
  fetchData,
  isFetching,
  hasSaveEndpoint,
  saveData,
  isSaving,
  isDirty,
  subscribe,
} from './dataFetcher';

// TODO: Remove this empty object constant.
const EMPTY_OBJECT = {};

type RemoveIndex<O> = {
  [K in keyof O as string extends K
    ? never
    : number extends K
    ? never
    : K]: O[K];
};

type FromDataProps<DataProps extends Dependencies> = {
  [K in keyof DataProps]:
    | ResourceTypeForKey<DataProps[K]['dataStoreKey']>
    | undefined;
};

type FromSaveProps<SaveProps extends Dependencies> = {
  [K in keyof SaveProps]: () => Promise<ObjectOf<JSONValue>>;
};

type FromComputedProps<Computed extends ComputedPropList> = {
  [K in keyof Computed]: ReturnType<Computed[K]['compute']> | undefined;
};

type Transformer = (value: unknown) => unknown;
type ComputeFunc = (...args: Array<any>) => any;
type ShouldFetchFunc = (
  dataStore: DataStoreInstance<'app'>,
  resolvedKey: string,
) => boolean;
type Dependency = {
  dataStoreKey: ResourceKey;
  params?: Array<string>;
  // Transform the value from the dataStore before it gets to the component.
  transform?: Transformer;
  shouldFetch?: ShouldFetchFunc;
};
type Dependencies = {
  [propName: string]: Dependency;
};
type NormalizedDep = {
  propName: string;
  key: string;
  params: Array<string>;
  resolvedKey: string;
  transform?: Transformer | undefined;
  shouldFetch?: ShouldFetchFunc | undefined;
};
type NormalizedDeps = Array<NormalizedDep>;
type MemoCache = Map<
  (...args: Array<any>) => any,
  { arg?: unknown; result?: unknown }
>;
type SaveProps = {
  [key: string]: () => void;
};
type Result = {
  args: Array<unknown>;
  compute: ComputeFunc;
  result: unknown;
};
type Cache = {
  lastDataProps?: {
    [key: string]: unknown;
  };
  lastSavedProps?: { arg?: Dependencies; value?: SaveProps };
  lastComputedProps?: {
    [propName: string]: Result;
  };
  lastComputedPropsResults?: {
    [propName: string]: unknown;
  };
  transformCache?: MemoCache;
  normalizedDeps?: Map<
    {
      [key: string]: any;
    },
    NormalizedDeps
  >;
};
type Data = {
  [key: string]: unknown;
};
type Props = {
  [key: string]: unknown;
};
type ComputedPropDescr = {
  props: Array<string>;
  compute: ComputeFunc;
};
type ComputedPropList = {
  [propName: string]: ComputedPropDescr;
};

let dataStore = DataStore.getInstance('app');

type FetchingProps = {
  isLoading: boolean;
  isSaving: boolean;
  isDirty: boolean;
  fetchAll: () => Promise<any>;
  saveAll: () => Promise<any>;
};

export type ConnectedProps<Connector> = Connector extends (
  c: ComponentType<infer P>,
) => any
  ? P
  : never;

export default function connectData<PassthruProps>() {
  return <
    DataProps extends Dependencies,
    SaveProps extends Dependencies,
    ComputedProps extends ComputedPropList
  >(
    mapDataToProps: ((ownProps: PassthruProps) => DataProps) | null | undefined,
    mapSaveToProps?:
      | ((ownProps: PassthruProps) => SaveProps)
      | null
      | undefined,
    options?: { computedProps: ComputedProps },
  ) => (
    OldComponent: ComponentType<
      Expand<
        PassthruProps &
          FromDataProps<RemoveIndex<DataProps>> &
          FromSaveProps<RemoveIndex<SaveProps>> &
          FromComputedProps<RemoveIndex<ComputedProps>> &
          FetchingProps
      >
    >,
  ): ComponentType<PassthruProps> => {
    let displayName =
      OldComponent.displayName || OldComponent.name || 'Anonymous';
    const propsToCompute = options?.computedProps ?? ({} as ComputedProps);
    const ExistingComponent = OldComponent as ComponentType<any>;
    class NewComponent extends Component<Props> {
      _cache: Cache = {}; // Used for memoization.
      _dataProps: Dependencies = EMPTY_OBJECT;
      _saveProps: Dependencies = EMPTY_OBJECT;
      _hasDataEndpoint = false;
      _hasSaveEndpoint = false;
      _subscriptions: Array<Subscription> = [];
      _fetchStatusSubscription: Subscription;

      constructor(props: Props) {
        super(props);
        autobind(this);
        this._fetchStatusSubscription = subscribe(() =>
          this._onFetchStatusChange(),
        );
      }

      componentWillMount() {
        // TODO: Replace this with a non-deprecated lifecycle method.
        // Note: This is challenging because this._initProps(...) will do
        // two things: initialize this._dataProps (which we need for rendering)
        // and kick off fetch requests. Since fetching is a side effect, we
        // want to do that in componentDidMount, but this._dataProps is
        // computed from props and is necessary for render, so needs to be done
        // in constructor, render or getDerivedStateFromProps.
        // So these two things (computing stuff from props and fetching) need
        // to be split up. This will be a significant refactor.
        this._initProps(this.props);
        document.addEventListener('routeChange', this._onRouteChange);
      }

      componentWillReceiveProps(newProps: Props) {
        // TODO: Replace this with a non-deprecated lifecycle method.
        // Note: See above for why this is complex.
        let oldProps = this.props;
        if (!shallowEqual(oldProps, newProps)) {
          this._initProps(newProps);
        }
      }

      componentWillUnmount() {
        if (this._fetchStatusSubscription) {
          this._fetchStatusSubscription.unsubscribe();
        }
        this._subscriptions.forEach((subscription) => {
          subscription.unsubscribe();
        });
        document.removeEventListener('routeChange', this._onRouteChange);
      }

      _initProps(props: Props) {
        this._initDataProps(props);
        this._initSaveProps(props);
      }

      _initDataProps(props: Props) {
        let newDataProps = mapDataToProps
          ? mapDataToProps(props as any)
          : EMPTY_OBJECT;
        let { isEqual, propsToFetch } = compareDependencies(
          this._dataProps,
          newDataProps,
        );
        if (isEqual) {
          return;
        }
        this._dataProps = newDataProps;
        let subscriptions = this._subscriptions;
        while (subscriptions.length) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          subscriptions.pop()!.unsubscribe();
        }
        let hasEndpoint = false;
        let initFetches = [];
        for (let {
          propName,
          key,
          params,
          resolvedKey,
          shouldFetch,
        } of normalizeDeps(this._dataProps, this._cache)) {
          subscriptions.push(
            dataStore.on(`change:${resolvedKey}`, () => this.forceUpdate()),
          );
          if (hasFetchEndpoint(key)) {
            hasEndpoint = true;
            if (propsToFetch.has(propName)) {
              if (!shouldFetch || shouldFetch(dataStore, resolvedKey)) {
                initFetches.push(() => fetchData(key, params));
              }
            }
          }
        }
        initFetches.forEach((fetch) => fetch());
        this._hasDataEndpoint = hasEndpoint;
      }

      _initSaveProps(props: Props) {
        this._saveProps = mapSaveToProps
          ? mapSaveToProps(props as any)
          : EMPTY_OBJECT;
        let hasEndpoint = false;
        for (let { key } of normalizeDeps(this._saveProps, this._cache)) {
          if (hasSaveEndpoint(key)) {
            hasEndpoint = true;
          }
        }
        this._hasSaveEndpoint = hasEndpoint;
      }

      render() {
        let dataProps = getDataProps(this._dataProps, this._cache);
        let saveProps = getSaveProps(this._saveProps, this._cache);
        let props = { ...this.props, ...dataProps };
        return (
          <ExistingComponent
            {...props}
            {...computeProps(propsToCompute, props, this._cache)}
            {...saveProps}
            isLoading={this._isLoading()}
            isSaving={this._isSaving()}
            isDirty={this._isDirty()}
            fetchAll={this._fetchAll}
            saveAll={this._saveAll}
          />
        );
      }

      _fetchAll() {
        let promises = [];
        for (let { key, params } of normalizeDeps(
          this._dataProps,
          this._cache,
        )) {
          if (hasFetchEndpoint(key)) {
            promises.push(fetchData(key, params));
          }
        }
        return Promise.all(promises);
      }

      _saveAll() {
        let promises = [];
        for (let { key, params } of normalizeDeps(
          this._dataProps,
          this._cache,
        )) {
          promises.push(saveData(key, params));
        }
        let promise = Promise.all(promises);
        promise.catch(showSaveError).then(showSaveSuccess);
        return promise;
      }

      _isLoading() {
        if (this._hasDataEndpoint) {
          for (let { key, params } of normalizeDeps(
            this._dataProps,
            this._cache,
          )) {
            if (isFetching(key, params)) {
              return true;
            }
          }
        }
        return false;
      }

      _isSaving() {
        if (this._hasSaveEndpoint) {
          for (let { key, params } of normalizeDeps(
            this._dataProps,
            this._cache,
          )) {
            if (isSaving(key, params)) {
              return true;
            }
          }
        }
        return false;
      }

      _isDirty() {
        if (this._hasDataEndpoint) {
          for (let { key, params } of normalizeDeps(
            this._dataProps,
            this._cache,
          )) {
            if (isDirty(key, params)) {
              return true;
            }
          }
        }
        return false;
      }

      _onFetchStatusChange() {
        if (this._hasDataEndpoint || this._hasSaveEndpoint) {
          // TODO: Be smarter about when to force an update.
          this.forceUpdate();
        }
      }

      _onRouteChange(event: any) {
        // Navigation will only be blocked when all of these conditions are fulfilled:
        // 1. The route has specified an endpoint to save the changes.
        // To do this, explicitly pass `mapSaveToProps` to connectData.
        // e.g.
        // ```
        // let mapDataToProps = () => ({
        //      account: {dataStoreKey: 'account'},
        //      placement: {dataStoreKey: 'placement'},
        //  })
        // let mapSaveToProps = () => ({
        //      savePlacement: {dataStoreKey: 'placement'},
        //  })
        //  connectData(mapDataToProps, mapSaveToProps)(MyComponent);
        // ```
        // 2. The data passed to the route from `mapDataToProps` has been modified
        // One way to do this is to set the data on the DataStore.
        // e.g.
        // ```
        // let datastore = DataStore.get('app');
        //  datastore.set('account', newAccount);
        // ```
        if (!this._hasSaveEndpoint || !this._isDirty()) {
          return;
        }
        event.preventDefault();
        // Function on the route change event to allow us to continue later.
        const { proceed } = event;
        const CANCEL = t('Cancel');
        const DISCARD = t('Discard Changes');
        const SAVE = t('Save Changes');
        showDialog(
          t('Save Changes?'),
          t(
            'You are about to navigate away from this page but you have un-saved changes.',
          ),
          [CANCEL, DISCARD, SAVE],
          (value) => {
            if (value === CANCEL) {
              return;
            }
            // This is hacky. We will unmount soon since we have not cancelled the
            // route change. So don't update our UI with loading indicator. This
            // fixes a bug with CKEditor when mounted and unmounted quickly.
            // TODO: Fix this or re-enable line below.
            // this._suppressLoadingIndicator = true;
            if (value === SAVE) {
              this._saveAll().then(() => proceed());
            } else {
              this._fetchAll().then(() => proceed());
            }
          },
        );
      }
    }
    (NewComponent as any).displayName = `ConnectData(${String(displayName)})`;
    return NewComponent as any;
  };
}

export function compareDependencies(
  oldDeps: Dependencies = EMPTY_OBJECT,
  newDeps: Dependencies,
): { isEqual: boolean; propsToFetch: Set<string> } {
  let propNames = Object.keys(newDeps);
  let isEqual = propNames.length === Object.keys(oldDeps).length;
  let propsToFetch = new Set<string>();
  for (let propName of propNames) {
    if (
      !isDependencyEqual(oldDeps[propName] as any, newDeps[propName] as any)
    ) {
      isEqual = false;
      propsToFetch.add(propName);
    }
  }
  return { isEqual, propsToFetch };
}

export function isDependencyEqual(
  oldDep: Dependency,
  newDep: Dependency,
): boolean {
  if (oldDep == null || newDep == null) {
    return oldDep === newDep;
  }
  if (oldDep.dataStoreKey !== newDep.dataStoreKey) {
    return false;
  }
  let oldParams = oldDep.params || [];
  let newParams = newDep.params || [];
  if (oldParams.length !== newParams.length) {
    return false;
  }
  for (let i = 0; i < oldParams.length; i++) {
    if (oldParams[i] !== newParams[i]) {
      return false;
    }
  }
  return true;
}

export function computeProps(
  propsToCompute: ComputedPropList,
  dataProps: Data,
  cache: Cache,
): Props {
  let propNames = Object.keys(propsToCompute);
  if (propNames.length === 0) {
    return EMPTY_OBJECT;
  }
  let results: ObjectOf<any> = {};
  let lastResults = cache.lastComputedProps || (cache.lastComputedProps = {});
  let hasAnyChanged = Object.keys(lastResults).length !== propNames.length;
  for (let propName of propNames) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    let { props, compute } = propsToCompute[propName]!;
    let lastResult =
      lastResults[propName] || (lastResults[propName] = getEmptyResult());
    let lastArgs = lastResult.args || [];
    let hasChanged =
      props.length !== lastArgs.length || compute !== lastResult.compute;
    let args = props.map((name, i) => {
      let value = dataProps[name];
      if (!hasChanged && value !== lastArgs[i]) {
        hasChanged = true;
      }
      return value;
    });
    if (hasChanged) {
      let result = (results[propName] = compute(...args));
      lastResult.args = args;
      lastResult.compute = compute;
      lastResult.result = result;
      hasAnyChanged = true;
    } else {
      results[propName] = lastResult.result;
    }
  }
  if (!hasAnyChanged && cache.lastComputedPropsResults) {
    results = cache.lastComputedPropsResults;
  }
  return (cache.lastComputedPropsResults = results);
}

// Note: This function uses the global `dataStore`.
export function getDataProps(deps: Dependencies, cache: Cache): Data {
  let data: ObjectOf<unknown> = {};
  let lastData = cache.lastDataProps;
  let hasChanged = false;
  for (let { propName, resolvedKey, transform } of normalizeDeps(deps, cache)) {
    let value: unknown = dataStore.get(resolvedKey as any);
    let transformedValue = getTransformedValue(value, transform, cache);
    if (!lastData || transformedValue !== lastData[propName]) {
      hasChanged = true;
    }
    data[propName] = transformedValue;
  }
  if (hasChanged) {
    cache.lastDataProps = data;
    return data;
  } else {
    return lastData || EMPTY_OBJECT;
  }
}

// Note: This function uses the global `saveData`.
export function getSaveProps(deps: Dependencies, cache: Cache): SaveProps {
  let { lastSavedProps } = cache;
  if (lastSavedProps) {
    if (lastSavedProps.arg === deps && lastSavedProps.value != null) {
      return lastSavedProps.value;
    }
  } else {
    lastSavedProps = cache.lastSavedProps = {};
  }
  let saveProps: ObjectOf<() => void> = {};

  for (let { propName, key, params } of normalizeDeps(deps, cache)) {
    saveProps[propName] = () => {
      let promise = saveData(key, params);
      // TODO: This promise will never reject because fetchData swallows errors
      // (there's a TODO in that function).
      // So in certain cases (e.g. endpoint returns status 4xx) saveData will
      // show its own error toast and then this success toast will also show.
      // I think the workaround is to check, in the success handler, for empty (undefined) data.
      promise.catch(showSaveError).then(showSaveSuccess);
      return promise;
    };
  }
  lastSavedProps.arg = deps;
  lastSavedProps.value = saveProps;
  return saveProps;
}

export function getTransformedValue(
  value: unknown,
  transform: Transformer | null | undefined,
  cache: Cache,
): unknown {
  if (!transform) {
    return value;
  }
  let transformCache =
    cache.transformCache || (cache.transformCache = new Map());
  return memo(transform, value, transformCache);
}

export function memo(
  fn: (arg: unknown) => unknown,
  arg: unknown,
  cache: MemoCache,
): unknown {
  let cached = cache.get(fn);
  if (cached) {
    if (arg === cached.arg) {
      return cached.result;
    }
  } else {
    cached = {};
    cache.set(fn, cached);
  }
  let result = fn(arg);
  cached.arg = arg;
  cached.result = result;
  return result;
}

export function normalizeDeps(
  dependencies: Dependencies,
  cache: Cache,
): NormalizedDeps {
  let cacheMap = cache.normalizedDeps || (cache.normalizedDeps = new Map());
  let cachedResult = cacheMap.get(dependencies);
  if (cachedResult != null) {
    return cachedResult;
  }
  let result = Object.entries(dependencies).map(([propName, dependency]) => {
    let { dataStoreKey, params, transform, shouldFetch } = dependency;
    let key = dataStoreKey || propName;
    return {
      propName,
      key,
      params: params || [],
      resolvedKey: params ? resolveKey(key, params) : key,
      transform,
      shouldFetch,
    };
  });
  cacheMap.set(dependencies, result);
  return result;
}

function getEmptyResult(): Result {
  return {
    args: [],
    compute: () => {},
    result: null,
  };
}

function showSaveError(error: Error) {
  if (error && error.message) {
    showError(error.message);
  }
}

function showSaveSuccess() {
  showSuccess(t('Saved Successfully.'));
}
